import { observer } from 'mobx-react-lite'
import { capitalize } from 'lodash'
import { Plus as PlusIcon } from 'iconoir-react'
import { ContactBoardComponent } from 'app/components/action-center/ContactBoardComponent'
import { isSystemPipelineId } from 'app/components/action-center/systemPipelines'
import { TreatmentBoardComponent } from 'app/components/action-center/TreatmentBoardComponent'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { PipelineIcon } from 'app/components/Icon/PipelineIcon'
import { PageHeader } from 'app/components/PageHeader'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { AddContactButton } from 'app/components/pipelines/AddContactButton'
import { AddPipeline } from 'app/components/pipelines/AddPipeline'
import { PipelineLayout } from 'app/components/pipelines/PipelineLayout'
import { showModal } from 'app/lib/modal'
import type { UsePipelinesPageReturn } from './usePipelinesPage'

export type PipelinesComponentProps = UsePipelinesPageReturn & {
  openCreateModal: () => void
}

/**
 * Which queue Hygiene's and Leads's boards draw. Each maps to exactly one
 * `ActionQueueDef`. Treatment is not in here: it has four band queues (see
 * treatmentBands.ts) and none of them means "everything", so it is handled
 * separately below by mounting `TreatmentBoardComponent`, which always reads
 * its own `?band=` (see `bandFromParamOrDefault` there). A bare
 * `/pipelines?tab=system:treatment` shows the whole unscheduled-treatment
 * population by default and lets the component's own band control narrow it,
 * and a `treatment-fresh` card's `/pipelines?tab=system:treatment&band=fresh`
 * link needs nothing forwarded from here to land pre-narrowed.
 *
 * Hygiene's three windows (see hygieneBands.ts) are handled the same way,
 * just inside `ContactBoardComponent` rather than a component of their own:
 * `due-for-hygiene` here is only the anchor `ActionQueueDef` (its pipeline,
 * its columns), and the component reads its own `?band=` to decide which of
 * the three hygiene windows, or their union, is actually on screen.
 */
const SYSTEM_PIPELINE_QUEUE: Record<string, string> = {
  'system:leads': `active-leads`,
  'system:hygiene': `due-for-hygiene`,
}

export const PipelinesComponent = observer(function PipelinesComponent(props: PipelinesComponentProps) {
  const {
    contacts,
    activePipeline,
    focusedContact,
    openDrawer,
    contactTraversal,
    createPipeline,
    updatePipelineStage,
    deleteStage,
    addContactToPipeline,
    addContactsToPipeline,
    contactHandler,
    updateContactStage,
    removeContact,
    closeDrawerHandler,
    loadContact,
    openCreateModal,
  } = props
  const isMobile = useIsMobile()

  if (activePipeline) {
    const pipelineId = activePipeline.id ?? ``
    if (isSystemPipelineId(pipelineId)) {
      // These three draw the Action Center's own boards instead of the legacy
      // PipelineLayout, and none of this page's other furniture carries over:
      // - PageHeader: the board renders its own header (icon, name, strapline),
      //   so a second one above it would just repeat the title.
      // - AddContactButton: it writes a `pipelines` membership, which is
      //   meaningless for Active Leads (leadStatus-backed, never a pipeline
      //   membership (see systemPipelines.ts), and for Hygiene/Treatment, is
      //   exactly the write gated behind the board's own drag/move and reason
      //   modals elsewhere. A generic "add contact to stage 1" button would be
      //   a second, ungated door to the same write.
      // - ContactDrawer: usePipelinesPage never fetches a system pipeline's
      //   contacts (the board fetches its own, see the effect above), so this
      //   page's drawer state has nothing to open. TreatmentBoardComponent
      //   carries its own contact drawer for the "Contact" action;
      //   ContactBoardComponent has none today, a gap in the board itself
      //   rather than something this page's own drawer would have filled.
      return pipelineId === `system:treatment` ? (
        <TreatmentBoardComponent />
      ) : (
        <ContactBoardComponent queueId={SYSTEM_PIPELINE_QUEUE[pipelineId]} pipelineName={activePipeline.name} />
      )
    }

    return (
      // `px-10 pt-8` because this page is now an edge-scroll route (see
      // `edgeScrollPages` in AppLayout): `main` no longer carries the gutter,
      // and the system boards above re-add it inside <PageScroll>. This branch
      // has no PageScroll, so it puts back exactly what main used to give it.
      <div className="flex h-full flex-col px-10 pt-8">
        <PageHeader
          title={capitalize(activePipeline.name ?? `unnamed pipeline`)}
          totalItems={isMobile ? undefined : contacts.length}
          primaryButtonSlot={
            <AddContactButton pipeline={activePipeline} addContactsToPipeline={addContactsToPipeline} />
          }
        />
        <div className="flex h-full w-full grow flex-col overflow-hidden">
          <PipelineLayout
            activeSubpage={activePipeline.id!}
            contacts={contacts}
            pipeline={activePipeline}
            contactHandler={contactHandler}
            updatePipelineStage={updatePipelineStage}
            addContactToPipeline={addContactToPipeline}
            deleteStage={deleteStage}
            updateContactStage={updateContactStage}
            removeContact={removeContact}
          />
          <ContactDrawer
            openDrawer={openDrawer}
            contact={focusedContact}
            onClose={closeDrawerHandler}
            onChange={loadContact}
            nextContact={contactTraversal.next}
            previousContact={contactTraversal.prev}
            isNavigating={contactTraversal.isNavigating}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col px-10 pt-8">
      <div className="flex h-full w-full flex-col items-center justify-center">
        <PipelineIcon width={60} height={60} className="text-default" />
        <p className="mt-6 text-heading-xl text-strong">Create your first Pipeline</p>
        <p className="mt-4 text-center text-body-md text-default">You have no pipelines created.</p>
        <button
          className="mt-8 flex items-center gap-x-2 rounded-md border border-strong px-6 py-3"
          onClick={openCreateModal}
        >
          <PlusIcon width={16} height={16} className="text-default" />
          <p className="text-ui-sm text-default">Create Pipeline</p>
        </button>
      </div>
    </div>
  )
})
